import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import Dashboard, { useDashboard } from '../../Layouts/Dashboard';
import HeaderActions from '../../Components/HeaderActions';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import { format, parseISO, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// Define breadcrumbs for this page
const accountingDashboardBreadcrumbs = [
  { href: '/', label: 'Accueil' },
  { label: 'Comptabilité' }
];

const AccountingDashboardContent = () => {
  const { sendRequest } = useContext(HttpRequestContext);
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);

  // Mock data for demonstration
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      
      // Simulate API call with mock data
      setTimeout(() => {
        setDashboardData({
          totalBalance: 45678.90,
          monthlyIncome: 12345.67,
          monthlyExpenses: 8901.23,
          pendingTransactions: 23,
          unprocessedUploads: 5,
          documentsToReview: 12,
          balanceChange: 5.2,
          incomeChange: -2.1,
          expensesChange: 8.7,
          accountSummary: [
            { name: 'Compte Principal', balance: 25678.90, type: 'bank', currency: 'EUR' },
            { name: 'PayPal Business', balance: 12345.67, type: 'paypal', currency: 'EUR' },
            { name: 'Carte de Crédit', balance: -3456.78, type: 'credit', currency: 'EUR' },
            { name: 'Compte Épargne', balance: 10000.00, type: 'savings', currency: 'EUR' }
          ]
        });

        setRecentTransactions([
          {
            id: 1,
            date: '2024-01-15',
            description: 'Virement client ABC Corp',
            amount: 2500.00,
            type: 'income',
            account: 'Compte Principal',
            status: 'processed',
            hasDocument: true
          },
          {
            id: 2,
            date: '2024-01-14',
            description: 'Facture électricité',
            amount: -156.78,
            type: 'expense',
            account: 'Compte Principal',
            status: 'pending',
            hasDocument: false
          },
          {
            id: 3,
            date: '2024-01-13',
            description: 'Paiement PayPal - Vente en ligne',
            amount: 890.45,
            type: 'income',
            account: 'PayPal Business',
            status: 'processed',
            hasDocument: true
          },
          {
            id: 4,
            date: '2024-01-12',
            description: 'Achat matériel bureau',
            amount: -234.56,
            type: 'expense',
            account: 'Carte de Crédit',
            status: 'needs_document',
            hasDocument: false
          },
          {
            id: 5,
            date: '2024-01-11',
            description: 'Remboursement frais déplacement',
            amount: -89.30,
            type: 'expense',
            account: 'Compte Principal',
            status: 'processed',
            hasDocument: true
          }
        ]);

        setPendingDocuments([
          {
            id: 1,
            transactionId: 2,
            description: 'Facture électricité',
            amount: -156.78,
            dueDate: '2024-01-20',
            priority: 'medium'
          },
          {
            id: 2,
            transactionId: 4,
            description: 'Achat matériel bureau',
            amount: -234.56,
            dueDate: '2024-01-18',
            priority: 'high'
          },
          {
            id: 3,
            transactionId: null,
            description: 'Reçu restaurant - déjeuner client',
            amount: -45.80,
            dueDate: '2024-01-16',
            priority: 'low'
          }
        ]);

        setIsLoading(false);
      }, 1000);
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'dd MMM yyyy', { locale: fr });
  };

  const getAccountIcon = (type) => {
    switch (type) {
      case 'bank':
        return <AccountBalanceIcon />;
      case 'paypal':
        return <WalletIcon />;
      case 'credit':
        return <CreditCardIcon />;
      case 'savings':
        return <AccountBalanceIcon />;
      default:
        return <AccountBalanceIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'needs_document':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'processed':
        return 'Traité';
      case 'pending':
        return 'En attente';
      case 'needs_document':
        return 'Document requis';
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Chargement du tableau de bord...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <HeaderActions
        customActions={[
          <Button
            key="upload-transactions"
            variant="contained"
            startIcon={<UploadIcon />}
            component={Link}
            to="/accounting/upload-transactions"
            sx={{ mr: 1 }}
          >
            Importer Transactions
          </Button>,
          <Button
            key="upload-documents"
            variant="outlined"
            startIcon={<DescriptionIcon />}
            component={Link}
            to="/accounting/upload-documents"
          >
            Ajouter Documents
          </Button>
        ]}
      />

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Solde Total
                  </Typography>
                  <Typography variant="h5" component="div">
                    {formatCurrency(dashboardData.totalBalance)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {dashboardData.balanceChange > 0 ? (
                      <TrendingUpIcon color="success" fontSize="small" />
                    ) : (
                      <TrendingDownIcon color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={dashboardData.balanceChange > 0 ? 'success.main' : 'error.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {Math.abs(dashboardData.balanceChange)}% ce mois
                    </Typography>
                  </Box>
                </Box>
                <AccountBalanceIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Revenus du Mois
                  </Typography>
                  <Typography variant="h5" component="div">
                    {formatCurrency(dashboardData.monthlyIncome)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {dashboardData.incomeChange > 0 ? (
                      <TrendingUpIcon color="success" fontSize="small" />
                    ) : (
                      <TrendingDownIcon color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={dashboardData.incomeChange > 0 ? 'success.main' : 'error.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {Math.abs(dashboardData.incomeChange)}% vs mois dernier
                    </Typography>
                  </Box>
                </Box>
                <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Dépenses du Mois
                  </Typography>
                  <Typography variant="h5" component="div">
                    {formatCurrency(dashboardData.monthlyExpenses)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {dashboardData.expensesChange > 0 ? (
                      <TrendingUpIcon color="error" fontSize="small" />
                    ) : (
                      <TrendingDownIcon color="success" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={dashboardData.expensesChange > 0 ? 'error.main' : 'success.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {Math.abs(dashboardData.expensesChange)}% vs mois dernier
                    </Typography>
                  </Box>
                </Box>
                <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Actions Requises
                  </Typography>
                  <Typography variant="h5" component="div">
                    {dashboardData.documentsToReview}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Documents à traiter
                  </Typography>
                </Box>
                <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Account Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Résumé des Comptes
            </Typography>
            <Box sx={{ mt: 2 }}>
              {dashboardData.accountSummary.map((account, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    borderBottom: index < dashboardData.accountSummary.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getAccountIcon(account.type)}
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {account.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {account.type === 'credit' ? 'Solde dû' : 'Solde disponible'}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="h6"
                    color={account.balance < 0 ? 'error.main' : 'success.main'}
                  >
                    {formatCurrency(account.balance, account.currency)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Transactions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Transactions Récentes
              </Typography>
              <Button
                size="small"
                component={Link}
                to="/accounting/transactions"
              >
                Voir tout
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell align="center">Statut</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTransactions.slice(0, 5).map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(transaction.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {transaction.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {transaction.account}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={transaction.amount > 0 ? 'success.main' : 'text.primary'}
                          fontWeight="medium"
                        >
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(transaction.status)}
                          color={getStatusColor(transaction.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Voir détails">
                          <IconButton size="small">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!transaction.hasDocument && (
                          <Tooltip title="Ajouter document">
                            <IconButton size="small" color="warning">
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Pending Documents */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Documents en Attente
              </Typography>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                component={Link}
                to="/accounting/upload-documents"
              >
                Ajouter Documents
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell>Date Limite</TableCell>
                    <TableCell align="center">Priorité</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(doc.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(doc.dueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={doc.priority}
                          color={getPriorityColor(doc.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Traiter">
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// Main component wrapped with Dashboard layout
const AccountingDashboard = () => {
  return (
    <Dashboard 
      dashboardTitle="Tableau de Bord Comptabilité"
      breadcrumbs={accountingDashboardBreadcrumbs}
    >
      <AccountingDashboardContent />
    </Dashboard>
  );
};

export default AccountingDashboard; 